import { ReactElement, FocusEvent, useRef, useState, useCallback } from 'react';
import { IconButton, FormHelperText } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

import {
  DurationFieldProps,
  DurationFieldInternalKeys,
  DurationFieldInternalValue,
} from './types';
import { getInternalDefaultValue, getTotalSeconds } from './utils';
import {
  DurationFieldContainer,
  DurationFieldLabel,
  DurationFieldControl,
  DurationFieldFielSet,
  DurationFieldInputContainer,
  DurationFieldInputLabel,
  DurationFieldInputBase,
  DurationFieldInputColon,
} from './styles';

const DurationField = ({
  defaultValue,
  onChange,
  onBlur,
  error,
  helperText,
  label = 'Duracion',
  disabledClerable = false,
}: DurationFieldProps): ReactElement => {
  const containerRef = useRef<HTMLDivElement>(null);

  const [internalValue, setInternalValue] =
    useState<DurationFieldInternalValue>(getInternalDefaultValue(defaultValue));

  const [isClearable, setIsClearable] = useState(Boolean(defaultValue));

  const update = useCallback(
    (key: DurationFieldInternalKeys, value: string) => {
      const cleanValue = value.replace(/\D/g, '');

      const currentTotalSeconds = getTotalSeconds({
        ...internalValue,
        [key]: cleanValue,
      });

      setIsClearable(true);
      setInternalValue(getInternalDefaultValue(currentTotalSeconds));

      onChange?.(currentTotalSeconds);
    },
    [internalValue, onChange],
  );

  const reset = useCallback(() => {
    setIsClearable(false);

    setInternalValue({
      hours: '',
      minutes: '',
      seconds: '',
    });

    onChange?.(0);
  }, [onChange]);

  const handleBlur = useCallback(
    (event: FocusEvent<HTMLDivElement>) => {
      const nextFocus = event.relatedTarget as Node | null;

      if (!containerRef.current?.contains(nextFocus)) {
        onBlur?.();
      }
    },
    [onBlur],
  );

  return (
    <DurationFieldContainer ref={containerRef} onBlur={handleBlur}>
      {label && <DurationFieldLabel>{label}</DurationFieldLabel>}

      <DurationFieldControl>
        <DurationFieldFielSet>
          <DurationFieldInputContainer>
            <DurationFieldInputLabel>hs</DurationFieldInputLabel>

            <DurationFieldInputBase
              inputProps={{ maxLength: 2, inputMode: 'numeric' }}
              value={internalValue.hours}
              onChange={(event) => update('hours', event.target.value)}
              placeholder="0"
            />
          </DurationFieldInputContainer>

          <DurationFieldInputColon>:</DurationFieldInputColon>

          <DurationFieldInputContainer>
            <DurationFieldInputLabel>min</DurationFieldInputLabel>

            <DurationFieldInputBase
              inputProps={{ maxLength: 2, inputMode: 'numeric' }}
              value={internalValue.minutes}
              onChange={(event) => update('minutes', event.target.value)}
              placeholder="0"
            />
          </DurationFieldInputContainer>

          <DurationFieldInputColon>:</DurationFieldInputColon>

          <DurationFieldInputContainer>
            <DurationFieldInputLabel>seg</DurationFieldInputLabel>

            <DurationFieldInputBase
              inputProps={{ maxLength: 2, inputMode: 'numeric' }}
              value={internalValue.seconds}
              onChange={(event) => update('seconds', event.target.value)}
              placeholder="0"
            />
          </DurationFieldInputContainer>
        </DurationFieldFielSet>

        {!disabledClerable && (
          <IconButton onClick={reset} size="small" disabled={!isClearable}>
            <DeleteIcon />
          </IconButton>
        )}
      </DurationFieldControl>

      {helperText && (
        <FormHelperText error={error}>{helperText}</FormHelperText>
      )}
    </DurationFieldContainer>
  );
};

export default DurationField;
